import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { handleError, successToast } from "../../utils/common";
import { commonTableOptions } from "../../constants";
import useUserPermissions from "../../hooks/useUserPermissions";
import { BorrowerFormModal } from "./Modal/BorrowerFormModal";
import VisibilityIcon from "@mui/icons-material/Visibility";

const Borrowers = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const privateInstance = useAxiosInstance();
  const userPermissions = useUserPermissions();

  const moduleScope = useMemo(
    () =>
      userPermissions?.find((item: any) => item.permission_id === "borrowers")
        ?.scope,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const getData = async () => {
      const apiUrl = "borrowers";
      setLoading(true);
      try {
        const result = await privateInstance.get(`${API_VERSION.V3}/${apiUrl}`);
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setData(result?.data?.response || []);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [privateInstance]);

  const buildBorrowerPayload = (form: any) => {
    return {
      borrower_type: "PHARMA_DISTRIBUTOR",
      gst_no: form.gst_no,
      name: form.name,
      gst_name: form.gst_name,
      primary_email_id: form.email,
      contact_details: {
        address: form.address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        contact_no1: form.contact_no1,
        contact_no2: form.contact_no2,
        email_ids: [form.email],
      },
      business_pan_no: form.business_pan_no,
      status: "inactive",
      kyc_docs: {
        status: "pending",
      },
    };
  };

  const handleAddSupplier = async (formData: any) => {
    console.log("Supplier Payload:", formData);
    setLoading(true);
    const payload = buildBorrowerPayload(formData);
    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (result.data.status === API_RESULT.SUCCESS) {
        successToast(result.data.message);
        const newSupplier = result.data.response;
        setData((prev) => [newSupplier, ...prev]);
        setOpenAddModal(false);
      }
      // getData();
    } catch (error) {
      console.error("Error creating supplier:", error);
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      name: "borrower_id",
      label: "Borrower ID",
      options: { filter: false },
    },
    {
      name: "name",
      label: "Name",
      options: { filter: false },
    },
    {
      name: "gst_no",
      label: "GST Number",
      options: { filter: false, searchable: false },
    },
    {
      name: "business_pan_no",
      label: "PAN Number",
      options: { filter: false },
    },
    {
      name: "contact_details.address",
      label: "Address",
      options: { filter: false, display: false },
    },
    {
      name: "contact_details.city",
      label: "City",
      options: { filter: false, display: false },
    },
    {
      name: "contact_details.pincode",
      label: "Pincode",
      options: { filter: false, display: false },
    },
    {
      name: "address",
      label: "Location",
      options: {
        filter: false,
        searchable: false,
        download: false,
        customBodyRenderLite: (dataIndex: number) => {
          const address = data[dataIndex]?.contact_details;

          const city = address?.city || "--";
          const pincode = address?.pincode || "--";

          const displayValue =
            city !== "--" || pincode !== "--"
              ? `${city}${pincode !== "--" ? `, ${pincode}` : ""}`
              : "--";

          return (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 300,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayValue}
            </Typography>
          );
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        customBodyRender: (value: any) => {
          return (
            <>
              {value === "active" ? (
                <Chip
                  size="small"
                  label="Active"
                  sx={{ backgroundColor: "#C6F6D5", padding: "6px" }}
                />
              ) : value === "inactive" ? (
                <Chip
                  size="small"
                  label="Inactive"
                  sx={{ backgroundColor: "#FEEBC8", padding: "6px" }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Blocked"
                  sx={{
                    backgroundColor: "#C53030",
                    color: "#FFF",
                    padding: "6px",
                  }}
                />
              )}
            </>
          );
        },
      },
    },
    {
      name: "",
      label: "Actions",
      options: {
        download: false,
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => (
          <Button
            onClick={() =>
              window.open(
                `/borrowers/${data[tableMeta.rowIndex]?.borrower_id}`,
                "_blank",
              )
            }
            variant="contained"
            color="primary"
            size="small"
          >
            Manage
          </Button>
        ),
      },
    },
  ];

  const options: any = {
    ...commonTableOptions,
    searchPlaceholder: "Borrower ID / Name / Phone No",
    download: moduleScope.includes("edit"),
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "No data available"
        ),
      },
    },
    downloadOptions: {
      filename: `Borrowers_${new Date().toLocaleDateString()}`,
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      },
    },
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight={600}>
          All Borrowers
        </Typography>

        {moduleScope.includes("edit") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
          >
            Add Borrower
          </Button>
        )}
      </Stack>

      <MUIDataTable title="" data={data} columns={cols} options={options} />

      <BorrowerFormModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSubmit={handleAddSupplier}
      />
    </Box>
  );
};

export default Borrowers;
